/**
 * ChatMessage -- Monarch Message Bubble
 *
 * Simplified message component for the Monarch AI panel.
 * No Chart.js, no complex rendering -- just styled bubbles
 * with basic markdown parsing and relative timestamps.
 */

import type { ChatMessage } from '@/stores/chatStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MessageItemProps {
  message: ChatMessage;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Relative timestamp: "just now", "2m ago", "1h ago", "3d ago", etc.
 */
function relativeTime(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

/**
 * Basic markdown to HTML:
 *   **bold** -> <strong>
 *   *italic* -> <em>
 *   `code`   -> <code>
 *   ```...```-> <pre><code>
 */
function parseMarkdown(text: string): string {
  // Escape HTML entities first
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks (triple backtick)
  html = html.replace(
    /```(?:\w*)\n?([\s\S]*?)```/g,
    '<pre class="bg-[var(--color-code-bg)] border border-[var(--color-code-border)] rounded-lg px-3 py-2 my-2 overflow-x-auto text-xs font-mono text-[var(--color-code-text)]"><code>$1</code></pre>',
  );

  // Inline code (single backtick)
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="bg-[var(--color-code-bg)] border border-[var(--color-code-border)] rounded px-1.5 py-0.5 text-xs font-mono text-[var(--color-code-text)]">$1</code>',
  );

  // Bold (**text**)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-text-2">$1</strong>');

  // Italic (*text*)
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

  // Line breaks
  html = html.replace(/\n/g, '<br />');

  return html;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MessageItem({ message }: MessageItemProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`
          max-w-[85%] px-4 py-3
          ${isUser
            ? 'bg-monarch-gold text-white rounded-2xl rounded-br-sm'
            : 'bg-bg-2 border border-border rounded-2xl rounded-bl-sm text-text-2'
          }
        `}
      >
        {/* Message content */}
        <div
          className="text-sm leading-relaxed break-words [&_pre]:my-2 [&_code]:text-xs"
          dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }}
        />

        {/* Timestamp */}
        <div
          className={`
            text-[10px] mt-1.5 select-none
            ${isUser ? 'text-white/60 text-right' : 'text-text-3'}
          `}
        >
          {relativeTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}
